import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
    TextField, Divider, Stack, Grid, FormControlLabel, Checkbox,
    ToggleButtonGroup, ToggleButton
} from '@mui/material';

const PartSelectionSection = ({ title, parts, services, data, setData, vehicleType, category }) => {
    const [satisTipi, setSatisTipi] = useState('tekil');

    useEffect(() => {
        if (data.length > 0 && data[0]?.satisTipi === 'paket') {
            setSatisTipi('paket');
        }
    }, []);

    useEffect(() => {
        if (!vehicleType || data.length === 0) return;
        if (satisTipi === 'paket') return;

        const updatedData = data.map(partItem => {
            const selectedService = services.find(s => s.id === partItem.urunId);
            if (selectedService) {
                let newPrice = selectedService.fiyat || 0;
                if (selectedService.fiyatlar && selectedService.fiyatlar[vehicleType]) {
                    newPrice = selectedService.fiyatlar[vehicleType];
                }
                if (partItem.fiyat !== newPrice) {
                    return { ...partItem, fiyat: newPrice };
                }
            }
            return partItem;
        });

        if (JSON.stringify(updatedData) !== JSON.stringify(data)) {
            setData(updatedData);
        }
    }, [vehicleType, services, data, setData, satisTipi]);

    const paketHizmetler = services.filter(s => s.isPaket);
    const tekilHizmetler = services.filter(s => !s.isPaket);

    const handleSatisTipiChange = (_, newValue) => {
        if (!newValue) return;
        setSatisTipi(newValue);
        setData([]);
    };

    const handlePaketSecimi = (paketId) => {
        if (!paketId) { setData([]); return; }
        const paket = services.find(s => s.id === paketId);
        if (!paket) return;

        let paketFiyat = paket.fiyat || 0;
        if (vehicleType && paket.fiyatlar && paket.fiyatlar[vehicleType]) {
            paketFiyat = paket.fiyatlar[vehicleType];
        }

        const parcalar = (paket.parcaIcerikleri || []).map(p => ({
            ad: p.parcaAdi,
            urunId: p.urunId || '',
            urunAdi: p.urunAdi || '',
            fiyat: p.fiyat || 0
        }));

        if (parcalar.length === 0) {
            parts.forEach(part => {
                parcalar.push({ ad: part, urunId: '', urunAdi: '', fiyat: 0 });
            });
        }

        setData([{
            satisTipi: 'paket',
            paketId: paket.id,
            paketAdi: paket.hizmetAdi,
            paketFiyat: paketFiyat,
            fiyat: paketFiyat,
            parcalar: parcalar
        }]);
    };

    const handleParcaUrunDegistir = (parcaIndex, urunId) => {
        if (data.length === 0 || satisTipi !== 'paket') return;
        const updated = [...data];
        const parcalar = [...(updated[0].parcalar || [])];

        if (!urunId) {
            parcalar[parcaIndex] = { ...parcalar[parcaIndex], urunId: '', urunAdi: '', fiyat: 0 };
        } else {
            const service = tekilHizmetler.find(s => s.id === urunId);
            if (service) {
                let price = service.fiyat || 0;
                if (vehicleType && service.fiyatlar && service.fiyatlar[vehicleType]) {
                    price = service.fiyatlar[vehicleType];
                }
                parcalar[parcaIndex] = {
                    ...parcalar[parcaIndex],
                    urunId: service.id,
                    urunAdi: service.hizmetAdi,
                    fiyat: price
                };
            }
        }
        updated[0] = { ...updated[0], parcalar };
        setData(updated);
    };

    const handleParcaFiyatDegistir = (parcaIndex, newFiyat) => {
        if (data.length === 0 || satisTipi !== 'paket') return;
        const updated = [...data];
        const parcalar = [...(updated[0].parcalar || [])];
        parcalar[parcaIndex] = { ...parcalar[parcaIndex], fiyat: Number(newFiyat) };
        updated[0] = { ...updated[0], parcalar };
        setData(updated);
    };

    const handlePartChange = (part, field, value) => {
        const existingPartIndex = data.findIndex(p => p.ad === part);
        let newData = [...data];

        if (existingPartIndex > -1) {
            if (!value && field === "urunId") {
                newData.splice(existingPartIndex, 1);
            } else {
                newData[existingPartIndex][field] = value;
                if (field === "urunId") {
                    const selectedService = services.find(s => s.id === value);
                    if (selectedService) {
                        newData[existingPartIndex]['urunAdi'] = selectedService.hizmetAdi;
                        let calculatedPrice = selectedService.fiyat || 0;
                        if (vehicleType && selectedService.fiyatlar && selectedService.fiyatlar[vehicleType]) {
                            calculatedPrice = selectedService.fiyatlar[vehicleType];
                        }
                        newData[existingPartIndex]['fiyat'] = calculatedPrice;
                        if (selectedService.paketIcerikleri && selectedService.paketIcerikleri.length > 0) {
                            newData[existingPartIndex]['icindekiHizmetler'] = [...selectedService.paketIcerikleri];
                        } else {
                            newData[existingPartIndex]['icindekiHizmetler'] = [];
                        }
                    }
                }
            }
        } else {
            if (field === "urunId" && value) {
                const selectedService = services.find(s => s.id === value);
                if (selectedService) {
                    let calculatedPrice = selectedService.fiyat || 0;
                    if (vehicleType && selectedService.fiyatlar && selectedService.fiyatlar[vehicleType]) {
                        calculatedPrice = selectedService.fiyatlar[vehicleType];
                    }
                    const newItem = {
                        ad: part, urunId: value, urunAdi: selectedService.hizmetAdi || '',
                        fiyat: calculatedPrice, icindekiHizmetler: []
                    };
                    if (selectedService.paketIcerikleri && selectedService.paketIcerikleri.length > 0) {
                        newItem.icindekiHizmetler = [...selectedService.paketIcerikleri];
                    }
                    newData.push(newItem);
                }
            }
        }
        setData(newData);
    };

    const handleSubServiceChange = (part, subService, isChecked) => {
        const existingPartIndex = data.findIndex(p => p.ad === part);
        if (existingPartIndex === -1) return;
        let newData = [...data];
        const currentItem = newData[existingPartIndex];
        let currentSubServices = currentItem.icindekiHizmetler || [];

        if (isChecked) {
            const existingSubIndex = currentSubServices.findIndex(s => s.id === subService.id);
            if (existingSubIndex > -1) {
                currentSubServices[existingSubIndex] = subService;
            } else {
                currentSubServices.push(subService);
            }
        } else {
            currentSubServices = currentSubServices.filter(s => s.id !== subService.id);
        }
        newData[existingPartIndex]['icindekiHizmetler'] = currentSubServices;
        setData(newData);
    };

    const renderPaketSecimi = () => {
        const secilenPaket = data.length > 0 && data[0]?.satisTipi === 'paket' ? data[0] : null;

        return (
            <Box>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Paket Seçin</InputLabel>
                    <Select
                        value={secilenPaket?.paketId || ''}
                        label="Paket Seçin"
                        onChange={(e) => handlePaketSecimi(e.target.value)}
                    >
                        {paketHizmetler.map(s => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.hizmetAdi} — {(vehicleType && s.fiyatlar?.[vehicleType]) ? s.fiyatlar[vehicleType] : s.fiyat}₺
                            </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem value=""><em>- Seçimi Kaldır -</em></MenuItem>
                    </Select>
                </FormControl>

                {secilenPaket && (
                    <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main', bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="primary">
                                {secilenPaket.paketAdi} — Parça Detayları
                            </Typography>
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                                Paket: {(secilenPaket.paketFiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                            </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                            Alt ürün fiyatları bilgi amaçlıdır, paket fiyatını etkilemez. Ürün ve fiyatları manuel değiştirebilirsiniz.
                        </Typography>

                        <Stack spacing={1}>
                            {(secilenPaket.parcalar || []).map((parca, idx) => (
                                <Grid container spacing={1} key={parca.ad} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="body2" fontWeight="bold">{parca.ad}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Ürün</InputLabel>
                                            <Select
                                                value={parca.urunId || ''}
                                                label="Ürün"
                                                onChange={(e) => handleParcaUrunDegistir(idx, e.target.value)}
                                            >
                                                {tekilHizmetler.map(s => (
                                                    <MenuItem key={s.id} value={s.id}>{s.hizmetAdi}</MenuItem>
                                                ))}
                                                <Divider />
                                                <MenuItem value=""><em>- Seçimi Kaldır -</em></MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Fiyat" size="small" type="number" fullWidth
                                            value={parca.fiyat || ''}
                                            onChange={(e) => handleParcaFiyatDegistir(idx, e.target.value)}
                                            disabled={!parca.urunId}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">TL</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Box>
        );
    };

    const renderTekilSecim = () => (
        <Stack spacing={2}>
            {parts.map(part => {
                const currentPartData = data.find(p => p.ad === part);
                const selectedService = currentPartData ? services.find(s => s.id === currentPartData.urunId) : null;

                return (
                    <Box key={part}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4} md={3}>
                                <Typography>{part}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={5} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Ürün/Hizmet</InputLabel>
                                    <Select
                                        value={currentPartData?.urunId || ''}
                                        label="Ürün/Hizmet"
                                        onChange={(e) => handlePartChange(part, 'urunId', e.target.value)}
                                    >
                                        {tekilHizmetler.map(s => <MenuItem key={s.id} value={s.id}>{s.hizmetAdi}</MenuItem>)}
                                        <Divider />
                                        <MenuItem value=""><em>- Seçimi Kaldır -</em></MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3}>
                                <TextField
                                    label="Fiyat" size="small" type="number" fullWidth
                                    value={currentPartData?.fiyat || ''}
                                    onChange={(e) => handlePartChange(part, 'fiyat', Number(e.target.value))}
                                    disabled={!currentPartData}
                                />
                            </Grid>
                        </Grid>

                        {selectedService?.paketIcerikleri && selectedService.paketIcerikleri.length > 0 && (
                            <Box sx={{ mt: 2, ml: { xs: 0, sm: '8%' }, pl: 2, borderLeft: 2, borderColor: 'primary.light', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Paket İçeriği:</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                    Bu fiyatlar bilgilendirme amaçlıdır, ana fiyatı etkilemez.
                                </Typography>
                                <Stack spacing={1}>
                                    {selectedService.paketIcerikleri.map(subService => {
                                        const existingSubItem = currentPartData.icindekiHizmetler?.find(s => s.id === subService.id);
                                        const isSelected = !!existingSubItem;
                                        let displayPrice = 0;
                                        if (existingSubItem && existingSubItem.fiyat !== undefined) {
                                            displayPrice = existingSubItem.fiyat;
                                        } else {
                                            displayPrice = subService.fiyat || 0;
                                            if (vehicleType && subService.fiyatlar && subService.fiyatlar[vehicleType]) {
                                                displayPrice = subService.fiyatlar[vehicleType];
                                            }
                                        }

                                        return (
                                            <Grid container key={subService.id} alignItems="center" spacing={2} sx={{ opacity: isSelected ? 1 : 0.5 }}>
                                                <Grid item xs={8}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox size="small" checked={isSelected}
                                                                onChange={(e) => {
                                                                    handleSubServiceChange(part, { ...subService, fiyat: displayPrice }, e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography variant="body2">{subService.hizmetAdi}</Typography>}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        label="Alt Hizmet Fiyatı" size="small" type="number" fullWidth
                                                        disabled={!isSelected} value={displayPrice}
                                                        onChange={(e) => {
                                                            handleSubServiceChange(part, { ...subService, fiyat: Number(e.target.value) }, true);
                                                        }}
                                                        InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">TL</Typography> }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );

    const showToggle = category && paketHizmetler.length > 0;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{title}</Typography>

            {showToggle && (
                <Box sx={{ mb: 2 }}>
                    <ToggleButtonGroup
                        value={satisTipi}
                        exclusive
                        onChange={handleSatisTipiChange}
                        size="small"
                        color="primary"
                        fullWidth
                    >
                        <ToggleButton value="tekil">Tekil Ürün Seçimi</ToggleButton>
                        <ToggleButton value="paket">Paket Satışı</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mt: 2 }}>
                {satisTipi === 'paket' && showToggle ? renderPaketSecimi() : renderTekilSecim()}
            </Box>
        </Paper>
    );
};

export default PartSelectionSection;